import React from "react";
import { Partner } from "../../models/Partner";

const formatShortDate = (value?: string | null) => {
  if (!value) return "—";
  const dateOnly = value.trim().slice(0, 10);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateOnly);
  const date = match
    ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    : new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDisplayText = (value?: string | null) => {
  if (!value) return null;
  return value.replace(/&nbsp;/g, " ").trim();
};

const maritalLabels: Record<string, string> = {
  SOLTERO: "Soltero",
  CASADO: "Casado",
  "DIVORCIADO LEGALMENTE": "Divorciado legalmente",
  "SEPARADO LEGALMENTE": "Separado legalmente",
  VIUDO: "Viudo",
};

const SUMMARY_STYLE_ID = "profile-personal-summary-styles-v2";

const ProfilePersonalSummary = ({
  partner,
  onEdit,
  readOnly = false,
}: {
  partner: Partner;
  onEdit: () => void;
  readOnly?: boolean;
}) => {
  React.useEffect(() => {
    document.getElementById("profile-personal-summary-styles")?.remove();
    if (document.getElementById(SUMMARY_STYLE_ID)) return;
    const styleSheet = document.createElement("style");
    styleSheet.id = SUMMARY_STYLE_ID;
    styleSheet.textContent = `
      .profile-personal-summary {
        width: 100%;
        max-width: 100%;
        box-sizing: border-box;
      }
      .profile-personal-summary__row {
        display: grid;
        grid-template-columns: 120px 1fr;
        gap: 8px 12px;
        padding: 10px 0;
        border-bottom: 1px dashed #e2e8f0;
      }
      .profile-personal-summary__row--full {
        grid-template-columns: 1fr;
        gap: 4px 0;
      }
      @media (max-width: 575.98px) {
        .profile-personal-summary {
          padding: 16px !important;
          border-radius: 16px !important;
        }
        .profile-personal-summary__row,
        .profile-personal-summary__row--full {
          grid-template-columns: 1fr;
          gap: 4px 0;
        }
      }
    `;
    document.head.appendChild(styleSheet);
  }, []);

  const person = partner?.person;

  const rows = [
    { label: "Correo", value: person?.email },
    { label: "Teléfono", value: person?.phone },
    { label: "RFC", value: person?.rfc },
    { label: "NSS", value: person?.nss },
    { label: "Fecha de nacimiento", value: formatShortDate(person?.birthdate) },
    { label: "Género", value: person?.genere },
    {
      label: "Estado civil",
      value: maritalLabels[person?.maritalStatus] || person?.maritalStatus,
    },
    { label: "Escolaridad", value: person?.schooling?.name },
    {
      label: "Domicilio",
      value: formatDisplayText(person?.address),
      fullWidth: true,
    },
  ];

  return (
    <div
      className="profile-personal-summary"
      style={{
        background: "#ffffff",
        borderRadius: 20,
        padding: "20px 22px",
        boxShadow: "0 4px 20px rgba(15, 23, 42, 0.06)",
        minWidth: 0,
      }}
    >
      <div
        className="d-flex align-items-center justify-content-between"
        style={{ marginBottom: 16 }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: "1rem",
            fontWeight: 700,
            color: "#0f172a",
          }}
        >
          Datos generales
        </h3>
        {!readOnly && (
          <button
            type="button"
            className="btn btn-sm"
            onClick={onEdit}
            style={{
              background: "#F7B917",
              border: "none",
              color: "#1e293b",
              fontWeight: 600,
              borderRadius: 999,
              padding: "6px 14px",
            }}
          >
            <i className="fa-solid fa-pen me-1" />
            Editar
          </button>
        )}
      </div>

      <dl style={{ margin: 0 }}>
        {rows.map((row) => (
          <div
            key={row.label}
            className={
              row.fullWidth
                ? "profile-personal-summary__row profile-personal-summary__row--full"
                : "profile-personal-summary__row"
            }
          >
            <dt
              style={{
                margin: 0,
                fontSize: "0.75rem",
                fontWeight: 600,
                color: "#64748b",
                textTransform: "uppercase",
                letterSpacing: "0.03em",
              }}
            >
              {row.label}
            </dt>
            <dd
              style={{
                margin: 0,
                fontSize: "0.875rem",
                fontWeight: 500,
                color: "#0f172a",
                wordBreak: "break-word",
              }}
            >
              {row.value?.toString().trim() ? row.value : "—"}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

export default ProfilePersonalSummary;
